// The incoming-call ringtone: public/sounds/incoming-call.wav, a soft
// two-note chime synthesized for Lixani from sine tones (no third-party
// recording, nothing to license). Three seconds, ending in silence, so it
// loops without a click.
//
// Only one Lixani tab rings at a time: every open tab sees the same call, and
// several copies of the loop slightly out of step sound like a fault. A Web
// Lock decides which tab; where the browser has none, every tab rings.
//
// A page may play sound only once its user has interacted with it. A tab that
// has not cannot ring, so it lets the lock go and the other tabs try theirs.
//
// On a phone or tablet the chime plays only while the tab is on screen. A
// hidden page there is about to be frozen anyway, and meanwhile the push
// notifications ring with the system sound; both at once was what testers
// heard (16 Oct 2026). Come back to the tab while it still rings and the
// chime picks up. A desktop gets a call's push once, not repeated, so there
// the chime keeps ringing from a background tab too.

export const RINGTONE_SRC = '/sounds/incoming-call.wav';
const LOCK_NAME = 'lixani-ringtone';
const VOLUME = 0.6;
// A tab that missed the lock tries again once, in case its holder could not
// play.
const RETRY_MS = 500;

let audio = null;
let wanted = false;
let releaseLock = null;
let retryTimer = null;
let watchingVisibility = false;

// Matches the push worker's own phone test (isMobileSubscription), so the two
// agree on which devices the pushes ring.
const isPhoneOrTablet = () =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(
    (typeof navigator !== 'undefined' && navigator.userAgent) || ''
  );

const mayRingHere = () =>
  !isPhoneOrTablet() || document.visibilityState === 'visible';

const audioElement = () => {
  if (!audio) {
    audio = new Audio(RINGTONE_SRC);
    audio.loop = true;
    audio.volume = VOLUME;
  }
  return audio;
};

const release = () => {
  if (releaseLock) {
    releaseLock();
    releaseLock = null;
  }
};

const clearRetry = () => {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
};

const silence = () => {
  clearRetry();
  if (audio) {
    audio.pause();
    audio.currentTime = 0;
  }
  release();
};

const play = async () => {
  try {
    const element = audioElement();
    element.currentTime = 0;
    await element.play();
    // Stopped, or hidden, while play() was still starting.
    if (!wanted || !mayRingHere()) {
      element.pause();
      return false;
    }
    return true;
  } catch (error) {
    // Autoplay refused (no interaction with this tab yet), or no audio.
    return false;
  }
};

const ringHoldingLock = () =>
  navigator.locks.request(LOCK_NAME, { ifAvailable: true }, (lock) => {
    if (!lock || !wanted || !mayRingHere()) return undefined;
    return new Promise((resolve) => {
      releaseLock = resolve;
      play().then((playing) => {
        if (!playing) release();
      });
    });
  });

const ring = () => {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    play();
    return;
  }

  ringHoldingLock().catch(() => {});
  clearRetry();
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (wanted && mayRingHere() && !releaseLock) {
      ringHoldingLock().catch(() => {});
    }
  }, RETRY_MS);
};

const onVisibilityChange = () => {
  if (!wanted) return;
  if (mayRingHere()) {
    if (!releaseLock && (!audio || audio.paused)) ring();
  } else {
    silence();
  }
};

/** Start ringing in this tab, unless another tab already is. */
export const startRingtone = () => {
  if (wanted) return;
  wanted = true;

  if (!watchingVisibility && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    watchingVisibility = true;
  }

  if (mayRingHere()) ring();
};

/** Stop ringing: answered, dismissed, missed or hung up. */
export const stopRingtone = () => {
  wanted = false;
  silence();
};
